import React, { useContext, useEffect, useMemo, useRef, useState, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { getPlRevenueView } from '../../api/financials_pl';
import { getSalesPlan, putSalesPlanSection } from '../../api/salesPlan';
import ProductPlanPage, { PRODUCT_KINDS } from './ProductPlanPage';
import {
  defaultModel, sanitize, yearsOf, closedOf, joinedPlanByLine,
  productPlanOf, addProductCard, addProductRow,
  servicePlanOf, addServiceCard, delServiceCard, setServiceCardField,
  addServiceRow, delServiceRow, setServiceRowField,
  setServiceRowQtyYear, setServiceRowQtyMonth, productRowQtyMonth, productRowQtyYear,
  heldQtyMonth, setHeldQtyMonth, setHeldQtyYear,
  setProjectPct, projectPctTotal, annualVolPctTotal, serviceRowRevenueMonth, serviceRowCostMonth,
  cardBilledMonth, cardCollectedMonth, cardTermsDays,
  setServiceRowVolYear, setServiceRowPctMonth, annualVolPct, prestaRowEnv, convertPrestaCard,
  setPrepaidBilledMonth, prepaidBilledMonth,
  setHourbankQtyMonth, hourbankQtyMonth, hourbankBilledMonth,
  hourbankEntry, hourbankQtyAt, convertHourbankCard, idxFactor, setPrestaUnit, setPrestaEntry,
  prestaEntry, prestaValueMonth,
  prepaidEntry, prepaidQtyMonth, setPrepaidQtyMonth, convertPrepaidCard, prepaidBilledAt,
  recurringEntry, recurringValMonth, setRecurringValMonth, convertRecurringCard,
  hourbankValMonth, setHourbankValMonth, setHourbankValYear,
  serviceCardHoursMonth, servicePlanHoursMonth, serviceCardRevenueMonth, moveServiceCard,
} from './atterrissage';
import { parsePasteSeries } from './pasteSeries';
import './ProductPlan.css';

/* Section « Plan services » — le gabarit du Plan produits, avec des MODÈLES
   PRÉPARÉS : le « ＋ » propose six boutons, chacun pose une card déjà câblée.

     · Régie (jours-hommes | heures) — volumes × tarif ;
     · CA par EPT — CA annuel par EPT ÷ 12 × EPT productifs du mois ;
     · Récurrent / location — prix mensuel × quantité TENUE (saisie un mois,
       elle court jusqu'à la prochaine saisie : mandat, abonnement, location) ;
     · Service préfacturé (dont carnet d'heures) — montant (ou heures × tarif)
       étalé sur une période depuis le mois de facturation ;
     · Projet — valeur × % de réalisation par mois (Σ ≤ 100), coût en %.

   Chaque card : compte de CA, CONDITIONS DE PAIEMENT (champ enregistré — la
   connexion créances viendra du lot Finance), indexation pluriannuelle. Les
   coûts sont EXTERNES uniquement (les collaborateurs sont déjà comptés dans
   People) — note sur la colonne. CA/coûts rejoignent la même jointure que le
   Plan produits (`joinedPlanByLine`) : un seul moteur, ligne verrouillée au
   Plan des ventes. */

const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
const nf = new Intl.NumberFormat('de-CH');
const fmt = (n) => nf.format(Math.round(Number(n) || 0)).replace(/’|,/g, "'");
const fmt1 = (n) => {
  const x = Number(n) || 0;
  if (Math.abs(x) < 0.05) return '';
  return (Math.round(x * 10) / 10).toLocaleString('de-CH', { maximumFractionDigits: 1 }).replace(/’|,/g, "'");
};
const cleanNum = (v) => { const x = parseFloat(String(v).replace(/['\s’]/g, '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };

const RoCtx = createContext(false);

/* Icônes d'en-tête de card (12 px, trait fin) : horloge = conditions de
   paiement, registre = compte de chiffre d'affaires. Le libellé complet reste
   en infobulle (title). */
const IcTerms = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const IcAccount = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 5h16v14H4z" />
    <path d="M8 9h8M8 13h5" />
  </svg>
);


function Num({ value, onCommit, onSeries }) {
  const readOnly = useContext(RoCtx);
  const [draft, setDraft] = useState(null);
  const [foc, setFoc] = useState(false);
  const shown = draft != null ? draft
    : foc ? String(value == null || Number(value) === 0 ? '' : Number(value))
      : fmt1(value);
  if (readOnly) return <span className="spl-ro">{shown || '–'}</span>;
  return (
    <input
      className="spl-num" inputMode="decimal" placeholder="–" value={shown}
      onFocus={() => setFoc(true)}
      onChange={(e) => setDraft(e.target.value)}
      onPaste={onSeries ? (e) => {
        const vals = parsePasteSeries(e);
        if (vals) { e.preventDefault(); onSeries(vals); setDraft(null); }
      } : undefined}
      onBlur={() => { setFoc(false); if (draft == null) return; onCommit(cleanNum(draft)); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

/* Modèles proposés par le « ＋ » : kind + unité + fiche en 3 lignes standard
   (convient pour · inducteur mensuel · formule). */
/* Modèles proposés par le « ＋ » — TROIS tuiles (consolidation 2026-08-15,
   9 → 3 : le choix décrivait des variantes, pas des modèles économiques) :
     · Prestations — fusion Régie (h/JH) + CA par EPT + Projet + Contrat
       d'heures annuel : unité au choix (heures / jours-hommes / CHF) et mode
       de saisie « au mois » (rythme connu) ou « enveloppe % » (total connu,
       rythme inconnu — la grosse commande de 400 h) ;
     · Récurrent — absorbe Licences (un récurrent nommé) et les locations ;
     · Préfacturé — absorbe le Carnet d'heures (un préfacturé en heures × tarif).
   Les cards créées avec les ANCIENS modèles restent rendues telles quelles
   (LEGACY_KINDS) — aucune migration de données. */
const KINDS = (t) => [
  { id: 'presta', kind: 'presta', unit: 'h', label: t('scenarii:servicePlan.kPresta', 'Prestations'),
    fit: t('scenarii:servicePlan.kPrestaFit', 'régie, projets, contrats-cadres, LSE — au rythme ou en enveloppe'),
    ind: t('scenarii:servicePlan.kPrestaInd2', 'heures ou jours-hommes'),
    formula: t('scenarii:servicePlan.kPrestaF', 'volume × tarif — saisi au mois, ou enveloppe répartie en %') },
  { id: 'hourbank', kind: 'hourbank', unit: '', label: t('scenarii:servicePlan.kHourbank', 'Carnet d\'heures'),
    fit: t('scenarii:servicePlan.kHourbankFit', 'carnets prépayés d\'heures — support, TMA, régie packagée'),
    ind: t('scenarii:servicePlan.kHourbankInd2', 'heures'),
    formula: t('scenarii:servicePlan.kHourbankF', 'carnets × heures × tarif horaire — CA réparti sur la durée') },
  { id: 'prepaid', kind: 'prepaid', unit: '', label: t('scenarii:servicePlan.kPrepaid', 'Service préfacturé'),
    fit: t('scenarii:servicePlan.kPrepaidFit3', 'maintenance et abonnements annuels facturés d\'avance, avances sur honoraires'),
    ind: t('scenarii:servicePlan.kPrepaidInd2', 'nb de contrats'),
    formula: t('scenarii:servicePlan.kPrepaidF', 'facturé réparti sur la période') },
  { id: 'recurring', kind: 'recurring', unit: '', label: t('scenarii:servicePlan.kRecurring', 'Récurrent / location'),
    fit: t('scenarii:servicePlan.kRecurringFit2', 'mandats, abonnements, licences, objets loués'),
    ind: t('scenarii:servicePlan.kRecurringInd2', 'nb de contrats'),
    formula: t('scenarii:servicePlan.kRecurringF', 'quantité × prix mensuel') },
];

/* Libellés des modèles RETIRÉS du menu (cards existantes toujours rendues). */
const LEGACY_KINDS = (t) => ({
  'regie:jh': t('scenarii:servicePlan.kRegieJh', 'Régie (jours-hommes)'),
  'regie:h': t('scenarii:servicePlan.kRegieH', 'Régie (heures)'),
  'capacity:ept': t('scenarii:servicePlan.kCapacity', 'CA par EPT'),
  'project:': t('scenarii:servicePlan.kProject', 'Projet'),
  'recurring:lic': t('scenarii:servicePlan.kLicense', 'Licences'),
  'annualvol:': t('scenarii:servicePlan.kAnnualVol', 'Contrat d\'heures annuel'),
});

const TERMS = [0, 30, 45, 60, 90];
/* Les trois lectures d'un plan de services (2026-08-17) :
     · ca     — chiffre d'affaires reconnu (ce que porte le compte de résultat) ;
     · billed — facturé (ce qui crée la créance) ;
     · cash   — encaissé (facturé décalé des conditions de paiement de la card).
   Même moteur que le bilan : `cardCollectedMonth` reprend l'arithmétique du
   roulement des créances, sinon deux encaissements pour la même vente. */
const metricMonth = (model, card, metric, y, m) => (
  metric === 'billed' ? cardBilledMonth(model, card, true, y, m)
    : metric === 'cash' ? cardCollectedMonth(model, card, true, y, m)
      : serviceCardRevenueMonth(model, card, y, m)
);
const metricLabel = (metric, card, t) => {
  if (metric === 'billed') return t('scenarii:servicePlan.mBilled', 'Facturé');
  if (metric === 'cash') {
    const d = cardTermsDays(card);
    return `${t('scenarii:servicePlan.mCash', 'Encaissé')}${d ? ` · ${d} j` : ''}`;
  }
  return (card.kind === 'prepaid' || card.kind === 'hourbank')
    ? t('scenarii:servicePlan.revSpread', "Chiffre d'affaires réparti")
    : t('scenarii:productPlan.revenue', "Chiffre d'affaires");
};


export default function ServicePlanPage({ activeTenant, addToast, canRead = true, canWrite = true, embedded = false }) {
  const { t } = useTranslation();
  const tid = activeTenant?.id;
  const y0 = new Date().getFullYear();
  // Drag & drop : la card SOURCE est tenue dans une REF (pas un state) — un
  // re-rendu pendant dragstart remplace le nœud DOM saisi et Chrome annule
  // le drag (bug constaté 2026-08-16).
  const dragRef = useRef(null);
  const [dropId, setDropId] = useState(null);
  const [synthDetail, setSynthDetail] = useState(false);   // répartition par card
  const [confirmDel, setConfirmDel] = useState(null);   // card à supprimer (confirmation)
  const [model, setModel] = usePersistentValueLocal(`atterrissage_${tid || 'none'}`, defaultModel(y0));
  const [revLines, setRevLines] = useState(null);
  const [view, setView] = useState('m0');
  /* TROIS LECTURES du même plan (demande Olivier 2026-08-17) : le chiffre
     d'affaires reconnu (+ heures vendues), ce qui est FACTURÉ, et ce qui est
     ENCAISSÉ (facturation décalée des conditions de paiement de la card).
     Facturé et encaissé n'ont ni coût ni marge : ces lignes disparaissent. */
  const [metric, setMetric] = usePersistentValueLocal(`spl_metric_${tid || 'none'}`, 'ca');
  const [menuOpen, setMenuOpen] = useState(false);
  const [aggs, setAggs] = useState(new Set());
  const syncedFor = useRef(null);
  const serverHadContent = useRef(false);
  const saveTimer = useRef(null);

  const cur = model.currentYear || y0;
  const years = yearsOf(model);

  useEffect(() => {
    let alive = true;
    if (!tid) return () => {};
    syncedFor.current = null;
    getSalesPlan(tid)
      .then((r) => {
        if (!alive) return;
        const remote = r?.error ? null : r?.data?.data;
        if (remote && (Object.keys(remote.data || {}).length
          || (remote.productPlan?.cards || []).length
          || (remote.servicePlan?.cards || []).length)) {
          setModel(sanitize(remote, y0));
        }
        // `unwrap` ne rejette JAMAIS (contrat {data,error}) : sans ce test, une
        // lecture en échec armait quand même la sauvegarde et le modèle local
        // (souvent vide) écrasait le plan du serveur — cause des cards
        // disparues (2026-08-16). Lecture KO → on n'arme pas.
        if (!r?.error) {
          serverHadContent.current = !!(remote && (Object.keys(remote.data || {}).length
            || (remote.productPlan?.cards || []).length || (remote.servicePlan?.cards || []).length));
          syncedFor.current = tid;
        }
      })
      // Échec de lecture serveur → on N'ARME PAS la sauvegarde.
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid]);

  useEffect(() => {
    let alive = true;
    if (!tid) return () => {};
    getPlRevenueView(tid, cur).then((res) => {
      if (alive && !res.error) setRevLines((res.data && res.data.lines) || []);
    }).catch(() => { if (alive) setRevLines([]); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid, cur]);

  useEffect(() => {
    if (!tid || syncedFor.current !== tid || !canWrite) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Garde-fou : un modèle VIDE ne remplace jamais un plan serveur qui avait
      // du contenu (perte constatée le 2026-08-16).
      const empty = !Object.keys(model.data || {}).length
        && !(model.productPlan?.cards || []).length
        && !(model.servicePlan?.cards || []).length;
      if (empty && serverHadContent.current) return;
      putSalesPlanSection(tid, model, ['servicePlan', 'productPlan'],   // les cards produits vivent ici aussi (2026-09-12)
        // La jointure n'est recalculée QUE si les lignes de revenus sont
        // chargées : sinon `computed` partait vide et Finance ne voyait plus le
        // plan (règle « % d'une ligne » à 0 — constat 2026-08-16).
        (merged) => ((revLines || []).length
          ? joinedPlanByLine(merged, revLines.map((l) => l.id))
          : merged.computed)).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [model, tid, revLines]);

  const update = (fn) => {
    if (!canWrite) return;
    setModel((prev) => { const d = JSON.parse(JSON.stringify(prev)); fn(d); return d; });
  };
  /* Les deux modèles PRODUITS (unités, négoce) se créent d'ici aussi
     (2026-09-12) : leurs cards vivent dans `productPlan`, rendues plus bas. */
  const createCard = (k) => {
    update((d) => {
      if (k.product) { const c = addProductCard(d, k.kind); addProductRow(d, c.id); return; }
      const c = addServiceCard(d, k.kind, k.unit); addServiceRow(d, c.id);
    });
    setMenuOpen(false);
  };

  /* Export Excel — section SANS onglet : le classeur est bâti à partir des
     tableaux DÉJÀ rendus (`exportRef` de PageShell). On ne remonte pas la page
     hors écran : elle enregistre au fil de la frappe, un second montage serait
     un risque d'écriture. */
  const exportRef = useRef(null);
  const excel = useViewExcelExport({
    ref: exportRef,
    sheetName: t('scenarii:servicePlan.title', 'Plan services'),
    fileBase: t('scenarii:servicePlan.title', 'Plan services'),
    tenantName: activeTenant?.name,
    year: cur,
    addToast,
  });
  const exportOptions = useMemo(
    () => [{ id: 'excel', label: 'Excel', run: excel.run }],
    [excel.run],
  );

  useRegisterPageExports(exportOptions, !embedded);   // embarqué dans un hub, c'est lui qui exporte (2026-09-12)

  if (!canRead) {
    return (
      <PageShell timeless embedded={embedded} className="page-shell--flush" title={t('scenarii:servicePlan.title', 'Plan services')}>
        <div className="spl-empty">{t('scenarii:atterrissage.noAccess', "Vous n'avez pas accès au périmètre Ventes.")}</div>
      </PageShell>
    );
  }

  const isMonthly = view !== 'annual';
  const focusYear = view === 'm1' ? cur + 1 : cur;
  const closed = isMonthly && focusYear === cur ? closedOf(model, cur) : 0;
  const revAccounts = (revLines || []).filter((l) => l.section === 'revenue' && !l.hidden);
  const cards = servicePlanOf(model);
  const cardsProduits = productPlanOf(model);
  /* Colonne de mois : filet à la clôture + aplat ardoise sur la zone
     PRÉVISIONNELLE (mois ≥ clôture). Vue pluriannuelle : pas de frontière. */
  const splitCols = isMonthly && closed > 0 && closed < 12;
  const colCls = (c, base = 'spl-c-val') =>
    `${base}${splitCols && c === closed ? ' spl-bound' : ''}`;
  /* Bandeau au-dessus des mois : l'inducteur à gauche sur les mois RÉELS,
     « Prévisionnel » sur les mois ouverts. La colonne total reste neutre. */
  const indBand = (label, cols) => (splitCols ? (
    <>
      <th className="spl-band spl-band--real" colSpan={closed}>{t('scenarii:productPlan.realBand', 'Réalisé')}</th>
      <th className="spl-band spl-band--fore" colSpan={12 - closed}>{t('scenarii:productPlan.forecastBand', 'Prévisionnel')}</th>
      <th className="spl-ind" />
    </>
  ) : (
    <th className="spl-ind" colSpan={cols.length + (isMonthly ? 1 : 0)}>{label}</th>
  ));
  const kindLabel = (c) => (KINDS(t).find((k) => k.kind === c.kind && (k.unit || '') === (c.unit || ''))
    || KINDS(t).find((k) => k.kind === c.kind) || {}).label
    || LEGACY_KINDS(t)[`${c.kind}:${c.unit || ''}`] || LEGACY_KINDS(t)[`${c.kind}:`] || c.kind;

  const modelMenu = (
    <div className="sp-kinds">
      {[...KINDS(t), ...PRODUCT_KINDS(t).map((k) => ({ ...k, product: true }))].map((k) => (
        <button key={k.id} className="sp-kind" onClick={() => createCard(k)}>
          <b>{k.label}</b>
          <span className="sp-kind__ind">{t('scenarii:servicePlan.unitLbl', 'Unité').toLowerCase()} : {k.ind}</span>
          <span className="sp-kind__f">{t('scenarii:servicePlan.formula', 'formule')} : {k.formula}</span>
          <i className="sp-kind__fit">{t('scenarii:servicePlan.fitFor', 'convient pour')} : {k.fit}</i>
        </button>
      ))}
    </div>
  );

  /* Colonnes de PARAMÈTRES par modèle — la ZONE a une largeur fixe (240 px,
     cf. colgroup) que chaque modèle divise : les colonnes de mois s'alignent
     donc à l'identique d'une card à l'autre, quel que soit le modèle. */
  const paramCols = (card) => {
    if (card.kind === 'presta') {
      // La pilule (Heures | Jours-hommes | %) gouverne toute la card — plus de
      // sélection de mode par ligne (retour Olivier 2026-08-15). La colonne
      // Volume/an ne sert qu'en mode enveloppe %.
      const env = (card.entry || 'month') === 'env';
      if (card.unit === 'chf') {
        return [
          { key: '__vol', label: t('scenarii:servicePlan.valueK', 'Valeur (kCHF HT)') },
          { key: 'costPct', label: t('scenarii:servicePlan.costMatPctOf', 'Coût matériel (% du CA)'), ext: true },
        ];
      }
      const u = card.unit === 'jh' ? t('scenarii:servicePlan.perJh', 'Tarif / JH') : t('scenarii:servicePlan.perH', 'Tarif / h');
      const uc = card.unit === 'jh' ? t('scenarii:servicePlan.costMatJh', 'Coût matériel / JH') : t('scenarii:servicePlan.costMatH', 'Coût matériel / h');
      // Colonnes FIGÉES (2026-08-16) : Volume/an et Tarif sont toujours là,
      // quel que soit le mode — en mode Qté, le volume annuel est la somme des
      // mois (lecture seule), en mode % il se saisit.
      return [
        { key: '__vol', label: t('scenarii:servicePlan.volYear', 'Volume / an') },
        { key: 'price', label: t('scenarii:servicePlan.rate', 'Tarif HT'), title: u },
        { key: 'cost', label: t('scenarii:servicePlan.costMat', 'Coût matériel'), title: uc, ext: true },
      ];
    }
    if (card.kind === 'regie') {
      const u = card.unit === 'h' ? t('scenarii:servicePlan.perH', 'Tarif / h') : t('scenarii:servicePlan.perJh', 'Tarif / JH');
      const uc = card.unit === 'h' ? t('scenarii:servicePlan.costMatH', 'Coût matériel / h') : t('scenarii:servicePlan.costMatJh', 'Coût matériel / JH');
      return [
        { key: 'price', label: t('scenarii:servicePlan.rate', 'Tarif HT'), title: u },
        { key: 'cost', label: t('scenarii:servicePlan.costMat', 'Coût matériel'), title: uc, ext: true },
      ];
    }
    if (card.kind === 'capacity') {
      return [
        { key: 'annual', label: t('scenarii:servicePlan.annualPerFteS', 'CA / EPT (kCHF HT)'), title: t('scenarii:servicePlan.annualPerFte', 'CA annuel par EPT (en milliers de CHF)') },
        { key: 'cost', label: t('scenarii:servicePlan.costPerFteS', 'Coût / EPT (kCHF)'), title: t('scenarii:servicePlan.costPerFte', 'Coût annuel par EPT (en milliers de CHF)'), ext: true },
      ];
    }
    if (card.kind === 'recurring') {
      const e = recurringEntry(card);
      if (e === 'chf') {
        return [{ key: 'costPct', label: t('scenarii:servicePlan.costMatPct', 'Coût mat. %'), ext: true }];
      }
      if (e === 'env') {
        return [
          { key: '__recyear', label: <>kCHF HT /<br />an</>, title: t('scenarii:servicePlan.recYearT', 'Montant annuel du contrat (kCHF)') },
          { key: 'costPct', label: t('scenarii:servicePlan.costMatPct', 'Coût mat. %'), ext: true },
        ];
      }
      return [
        { key: 'price', label: <>CHF HT /<br />mois</>, title: t('scenarii:servicePlan.monthlyPrice', 'Prix mensuel par contrat (CHF)') },
        { key: 'cost', label: <>Coût /<br />mois</>, title: t('scenarii:servicePlan.monthlyCost', 'Coût externe mensuel par contrat (CHF)'), ext: true },
      ];
    }
    if (card.kind === 'prepaid') {
      // Chaque mode n'expose que ses paramètres (2026-08-16).
      const e = prepaidEntry(card);
      const dur = { key: 'period', label: <>Durée<br />(mois)</>, title: t('scenarii:servicePlan.period', 'Durée d\'étalement du revenu (mois)') };
      const cost = { key: 'costPct', label: t('scenarii:servicePlan.costMatPct', 'Coût mat. %'), ext: true };
      if (e === 'qty') {
        return [
          { key: 'unitValue', label: <>CHF HT /<br />contrat</>, title: t('scenarii:servicePlan.pkgValue3', 'Valeur facturée d\'un contrat (CHF)') },
          dur, cost,
        ];
      }
      if (e === 'env') {
        return [
          { key: '__pkgyear', label: t('scenarii:servicePlan.hbKchfYear', 'kCHF HT / an'), title: t('scenarii:servicePlan.pkgYearT', 'Enveloppe annuelle facturée (kCHF)') },
          dur, cost,
        ];
      }
      return [dur, cost];
    }
    if (card.kind === 'hourbank') {
      // Chaque mode n'expose QUE ses paramètres (décision 2026-08-16) :
      //   Qté    → h/carnet · tarif · durée
      //   Valeur → tarif · durée
      //   %      → valeur/an · tarif
      const e = hourbankEntry(card);
      // Coupure explicite : « Heures / carnet » et « Tarif / h » se lisent sur
      // deux lignes plutôt que d'être tronqués (2026-08-16).
      const rate = { key: 'price', label: <>Tarif HT<br />horaire</>, title: t('scenarii:servicePlan.hbRate', 'Tarif horaire (effectif)') };
      const dur = { key: 'period', label: t('scenarii:servicePlan.hbPeriodS', 'Durée'), title: t('scenarii:servicePlan.hbPeriod', 'Durée moyenne du carnet (mois)') };
      const cost = { key: 'costPct', label: t('scenarii:servicePlan.costMatPct', 'Coût mat. %'), ext: true };
      if (e === 'chf') return [rate, dur, cost];
      if (e === 'env') {
        return [
          { key: '__hbvalyear', label: t('scenarii:servicePlan.hbKchfYear', 'kCHF HT / an'), title: t('scenarii:servicePlan.hbValueYearT', 'Enveloppe annuelle facturée (kCHF)') },
          rate, cost,
        ];
      }
      return [
        { key: 'hours', label: <>Heures /<br />carnet</>, title: t('scenarii:servicePlan.hbHours', 'Heures par carnet') },
        rate, dur, cost,
      ];
    }
    if (card.kind === 'annualvol') {
      return [
        { key: '__vol', label: t('scenarii:servicePlan.volYear', 'Volume / an') },
        { key: 'price', label: t('scenarii:servicePlan.perH', 'Tarif HT / h') },
        { key: 'cost', label: t('scenarii:servicePlan.costMatH', 'Coût matériel / h'), ext: true },
      ];
    }
    return [
      { key: 'value', label: t('scenarii:servicePlan.valueK', 'Valeur (kCHF HT)') },
      { key: 'costPct', label: t('scenarii:servicePlan.costMatPctOf', 'Coût matériel (% du CA)'), ext: true },
    ];
  };

  const renderCard = (card) => {
    const rows = card.rows || [];
    const agg = aggs.has(card.id);
    const cols = isMonthly ? MONTHS.map((_, m) => m) : years;
    const params = paramCols(card);
    const rev = (r, c) => (isMonthly
      ? serviceRowRevenueMonth(model, card, r, focusYear, c)
      : Array.from({ length: 12 }, (_, m) => serviceRowRevenueMonth(model, card, r, c, m)).reduce((s, x) => s + x, 0));
    const cst = (r, c) => (isMonthly
      ? serviceRowCostMonth(model, card, r, focusYear, c)
      : Array.from({ length: 12 }, (_, m) => serviceRowCostMonth(model, card, r, c, m)).reduce((s, x) => s + x, 0));
    // Valeur SAISIE sur le mois c (isMonthly uniquement) — même valeur que la
    // cellule affiche/édite : quantité, % ou montant selon le modèle. Sert au
    // total de ligne (Σ des volumes saisis, jan→déc), pas au CA qui en découle
    // (des modèles distincts partagent la même unité de saisie que d'affichage).
    const rowEnv = (r) => card.kind === 'presta' && prestaRowEnv(card, r);
    const pEntry = card.kind === 'presta' ? prestaEntry(card) : null;
    const entryVal = (r, c) => (card.kind === 'presta'
      ? (rowEnv(r) ? annualVolPct(r, focusYear, c)
        : pEntry === 'chf' ? prestaValueMonth(model, card, r, focusYear, c) / 1000
          : productRowQtyMonth(r, focusYear, c))
      : card.kind === 'prepaid' ? (ppE === 'env' ? annualVolPct(r, focusYear, c)
        : ppE === 'qty' ? prepaidQtyMonth(r, focusYear, c)
          : prepaidBilledMonth(r, focusYear, c))
      : card.kind === 'hourbank' ? (hbE === 'env' ? annualVolPct(r, focusYear, c)
        : hbE === 'chf' ? hourbankValMonth(r, focusYear, c) / 1000
          : hourbankQtyMonth(r, focusYear, c))
        : card.kind === 'regie' ? productRowQtyMonth(r, focusYear, c)
          : card.kind === 'project' ? (Number(r.pct?.[focusYear]?.[c]) || 0)
            : card.kind === 'annualvol' ? annualVolPct(r, focusYear, c)
              : card.kind === 'recurring' ? (rcE === 'env' ? annualVolPct(r, focusYear, c)
                : rcE === 'chf' ? recurringValMonth(r, focusYear, c) / 1000
                  : heldQtyMonth(r, focusYear, c))
                : heldQtyMonth(r, focusYear, c));
    // La lecture choisie (CA · facturé · encaissé) pilote la ligne de synthèse
    // de la card ; les volumes saisis au-dessus ne changent jamais.
    const mAt = (c) => (isMonthly
      ? metricMonth(model, card, metric, focusYear, c)
      : MONTHS.reduce((s2, _, m) => s2 + metricMonth(model, card, metric, c, m), 0));
    const caAt = (c) => (metric === 'ca' ? rows.reduce((s, r) => s + rev(r, c), 0) : mAt(c));
    const crAt = (c) => rows.reduce((s, r) => s + cst(r, c), 0);
    const caY = cols.reduce((s, c) => s + caAt(c), 0);
    const crY = cols.reduce((s, c) => s + crAt(c), 0);
    const pct = caY ? `${(Math.round(1000 * (caY - crY) / caY) / 10).toLocaleString('de-CH')} %` : '';
    const volUnit = card.kind === 'presta' ? (card.unit === 'chf' ? null
      : card.unit === 'jh' ? t('scenarii:servicePlan.jhSold', 'Jours-hommes') : t('scenarii:servicePlan.hoursSold', 'Heures'))
      : card.kind === 'capacity' ? t('scenarii:servicePlan.fteShort', 'EPT')
      : card.kind === 'recurring' ? (recurringEntry(card) === 'qty' ? t('scenarii:servicePlan.contractsHeld', 'Contrats en cours') : null)
        : card.kind === 'regie' ? (card.unit === 'h' ? t('scenarii:servicePlan.hoursSold', 'Heures') : t('scenarii:servicePlan.jhSold', 'Jours-hommes'))
          : card.kind === 'annualvol' ? t('scenarii:servicePlan.hoursSold', 'Heures')
            : card.kind === 'prepaid' ? (prepaidEntry(card) === 'qty' ? t('scenarii:servicePlan.contractsSold', 'Contrats facturés') : null)
            : card.kind === 'hourbank' ? (hourbankEntry(card) === 'qty' ? t('scenarii:servicePlan.banksSold', 'Carnets vendus') : null)
              : card.kind === 'project' ? t('scenarii:servicePlan.pctDone', '% de réalisation') : null;
    const hbE = card.kind === 'hourbank' ? hourbankEntry(card) : null;
    const ppE = card.kind === 'prepaid' ? prepaidEntry(card) : null;
    const rcE = card.kind === 'recurring' ? recurringEntry(card) : null;
    // Une ligne se saisit en % : projet, contrat d'heures (legacy), ou n'importe
    // quel modèle dont la card est en mode « % » (2026-08-16 — le récurrent
    // n'affichait pas son signe %).
    const isPctRow = (r) => card.kind === 'annualvol' || rowEnv(r)
      || hbE === 'env' || ppE === 'env' || rcE === 'env';
    const pctTotOf = (r) => cols.reduce((a, c) => a + entryVal(r, c), 0);
    const pctOver = card.kind === 'project' ? rows.some((r) => projectPctTotal(r) > 100.0001)
      : card.kind === 'annualvol' ? rows.some((r) => annualVolPctTotal(r, focusYear) > 100.0001)
      : card.kind === 'presta' ? rows.some((r) => rowEnv(r) && annualVolPctTotal(r, focusYear) > 100.0001)
      : (hbE === 'env' || ppE === 'env' || rcE === 'env')
        ? rows.some((r) => annualVolPctTotal(r, focusYear) > 100.0001)
        : false;
    /* Ce que SONT les cellules de mois, en clair (kCHF, jours-hommes, %…). */
    const monthUnit = (() => {
      if (!isMonthly && (card.kind === 'prepaid' || card.kind === 'hourbank' || card.kind === 'project')) {
        return t('scenarii:servicePlan.uRevK', 'Chiffre d\'affaires (kCHF)');
      }
      if (card.kind === 'presta') {
        if (card.unit === 'chf' || pEntry === 'chf') return t('scenarii:servicePlan.uRevK', 'Chiffre d\'affaires (kCHF)');
        return card.unit === 'jh' ? t('scenarii:servicePlan.uJh', 'Jours-hommes vendus') : t('scenarii:servicePlan.uHours', 'Heures vendues');
      }
      if (card.kind === 'prepaid') {
        if (ppE === 'qty') return t('scenarii:servicePlan.uContractsSold', 'Contrats facturés');
        if (ppE === 'env') return t('scenarii:servicePlan.uBankPctVal', "% de l'enveloppe annuelle");
        return t('scenarii:servicePlan.uBilled', 'Facturation (kCHF)');
      }
      if (card.kind === 'hourbank') {
        if (hbE === 'chf') return t('scenarii:servicePlan.uBilled', 'Facturation (kCHF)');
        if (hbE === 'env') return t('scenarii:servicePlan.uBankPctVal', "% de l'enveloppe annuelle");
        return t('scenarii:servicePlan.banksSold', 'Carnets vendus');
      }
      if (card.kind === 'regie') {
        return card.unit === 'h' ? t('scenarii:servicePlan.uHours', 'Heures vendues') : t('scenarii:servicePlan.uJh', 'Jours-hommes vendus');
      }
      if (card.kind === 'capacity') return t('scenarii:servicePlan.fteShort', 'EPT');
      if (card.kind === 'recurring') {
        if (rcE === 'chf') return t('scenarii:servicePlan.uRevK', 'Chiffre d\'affaires (kCHF)');
        if (rcE === 'env') return t('scenarii:servicePlan.uBankPctVal', "% de l'enveloppe annuelle");
        return t('scenarii:servicePlan.uContractsHeld', 'Contrats en cours');
      }
      if (card.kind === 'annualvol') return t('scenarii:servicePlan.uVolPct', '% du volume mensualisé');
      return t('scenarii:servicePlan.pctDone', '% de réalisation');
    })();

    // Cellule de mois d'une LIGNE, selon le modèle. L'écriture d'UN mois est
    // partagée entre la saisie et le COLLAGE Excel (une série collée remplit
    // les mois suivants de la ligne à partir de la cellule visée).
    const monthCell = (r, c) => {
      const setM = card.kind === 'presta'
        ? (rowEnv(r) ? (d, m, v) => setServiceRowPctMonth(d, card.id, r.id, focusYear, m, v)
          : pEntry === 'chf'
            // Saisie en VALEUR : reconvertie en volume (le stockage reste le
            // volume — une seule source de vérité).
            ? (d, m, v) => {
              const k = (Number(r.price) || 0) * idxFactor(model, card, focusYear) / 1000;
              setServiceRowQtyMonth(d, card.id, r.id, focusYear, m, k ? (Number(v) || 0) / k : 0);
            }
            : (d, m, v) => setServiceRowQtyMonth(d, card.id, r.id, focusYear, m, v))
        : card.kind === 'prepaid' ? (ppE === 'env'
          ? (d, m, v) => setServiceRowPctMonth(d, card.id, r.id, focusYear, m, v)
          : ppE === 'qty'
            ? (d, m, v) => setPrepaidQtyMonth(d, card.id, r.id, focusYear, m, v)
            : (d, m, v) => setPrepaidBilledMonth(d, card.id, r.id, focusYear, m, v))
        : card.kind === 'hourbank' ? (hbE === 'env'
          ? (d, m, v) => setServiceRowPctMonth(d, card.id, r.id, focusYear, m, v)
          : hbE === 'chf'
            ? (d, m, v) => setHourbankValMonth(d, card.id, r.id, focusYear, m, (Number(v) || 0) * 1000)
            : (d, m, v) => setHourbankQtyMonth(d, card.id, r.id, focusYear, m, v))
          : card.kind === 'regie' ? (d, m, v) => setServiceRowQtyMonth(d, card.id, r.id, focusYear, m, v)
            : card.kind === 'project' ? (d, m, v) => setProjectPct(d, card.id, r.id, focusYear, m, v)
              : card.kind === 'annualvol' ? (d, m, v) => setServiceRowPctMonth(d, card.id, r.id, focusYear, m, v)
                : card.kind === 'recurring' ? (rcE === 'env'
                  ? (d, m, v) => setServiceRowPctMonth(d, card.id, r.id, focusYear, m, v)
                  : rcE === 'chf'
                    ? (d, m, v) => setRecurringValMonth(d, card.id, r.id, focusYear, m, (Number(v) || 0) * 1000)
                    : (d, m, v) => setHeldQtyMonth(d, card.id, r.id, focusYear, m, v))
                  : (d, m, v) => setHeldQtyMonth(d, card.id, r.id, focusYear, m, v);
      const series = (vals) => update((d) => vals.forEach((v, i) => { if (c + i < 12) setM(d, c + i, v); }));
      if (!isMonthly) {
        if (card.kind === 'prepaid' || (card.kind === 'hourbank' && hbE !== 'env') || card.kind === 'project') {
          return <span className="spl-ro">{fmt(rev(r, c)) === '0' ? '–' : fmt(rev(r, c))}</span>;
        }
        if (card.kind === 'annualvol' || rowEnv(r) || card.kind === 'hourbank') {
          return <Num value={r.volume?.[c]} onCommit={(v) => update((d) => setServiceRowVolYear(d, card.id, r.id, c, v))} />;
        }
        const val = (card.kind === 'regie' || card.kind === 'presta') ? productRowQtyYear(r, c) : heldQtyMonth(r, c, 11);
        const set = (card.kind === 'regie' || card.kind === 'presta')
          ? (v) => update((d) => setServiceRowQtyYear(d, card.id, r.id, c, v))
          : (v) => update((d) => setHeldQtyYear(d, card.id, r.id, c, v));
        return <Num value={val} onCommit={set} />;
      }
      // recurring / capacity : quantité TENUE — la cellule affiche la valeur
      // qui court (héritée du dernier mois saisi) ; une saisie pose un nouveau
      // palier qui vaudra pour les mois suivants.
      const num = <Num value={entryVal(r, c)} onCommit={(v) => update((d) => setM(d, c, v))} onSeries={series} />;
      // Contrat d'heures annuel : ce sont des % à répartir sur l'année, pas
      // des heures — le signe le dit (sinon on lit « 10 » comme une valeur
      // absolue, confondu avec heures/carnets des autres modèles).
      return isPctRow(r) ? <span className="spl-pctcell">{num}<i>%</i></span> : num;
    };

    const paramCell = (r, p) => {
      if (p.key === '__recyear') {
        const yy = isMonthly ? focusYear : cur;
        return <Num value={(Number(r.value?.[yy]) || 0) / 1000}
          onCommit={(v) => update((d) => setHourbankValYear(d, card.id, r.id, yy, (Number(v) || 0) * 1000))} />;
      }
      if (p.key === '__pkgyear') {
        const yy = isMonthly ? focusYear : cur;
        return <Num value={(Number(r.value?.[yy]) || 0) / 1000}
          onCommit={(v) => update((d) => setHourbankValYear(d, card.id, r.id, yy, (Number(v) || 0) * 1000))} />;
      }
      if (p.key === '__hbvalyear') {
        const yy = isMonthly ? focusYear : cur;
        return <Num value={(Number(r.value?.[yy]) || 0) / 1000}
          onCommit={(v) => update((d) => setHourbankValYear(d, card.id, r.id, yy, (Number(v) || 0) * 1000))} />;
      }
      if (p.key === '__vol') {
        const yy = isMonthly ? focusYear : cur;
        // Mode Qté : total dérivé des mois — affiché, pas saisi (sinon deux
        // sources de vérité pour le même volume).
        if (card.kind === 'presta' && !rowEnv(r)) {
          return <span className="spl-ro">{fmt1(productRowQtyYear(r, yy)) || '–'}</span>;
        }
        return <Num value={r.volume?.[yy]} onCommit={(v) => update((d) => setServiceRowVolYear(d, card.id, r.id, yy, v))} />;
      }
      if (card.kind === 'project' && p.key === 'value') {
        // Valeur du projet saisie et affichée en kCHF (stockage en CHF).
        return <Num value={(Number(r.value) || 0) / 1000} onCommit={(v) => update((d) => setServiceRowField(d, card.id, r.id, 'value', v * 1000))} />;
      }
      if (card.kind === 'capacity' && (p.key === 'annual' || p.key === 'cost')) {
        // CA / Coût annuel par EPT saisis et affichés en kCHF (stockage en CHF) —
        // sinon illisible (ex. 150'000 sans séparateur).
        return <Num value={(Number(r[p.key]) || 0) / 1000} onCommit={(v) => update((d) => setServiceRowField(d, card.id, r.id, p.key, v * 1000))} />;
      }
      return <Num value={r[p.key]} onCommit={(v) => update((d) => setServiceRowField(d, card.id, r.id, p.key, v))} />;
    };

    return (
      <section
        className={`spl-card${dropId === card.id ? ' is-dropover' : ''}`} key={card.id}
        onDragOver={(e) => { if (dragRef.current && dragRef.current !== card.id) { e.preventDefault(); if (dropId !== card.id) setDropId(card.id); } }}
        onDragLeave={() => { if (dropId === card.id) setDropId(null); }}
        onDrop={(e) => {
          e.preventDefault();
          const src = dragRef.current;
          if (src && src !== card.id) update((d) => moveServiceCard(d, src, card.id));
          dragRef.current = null; setDropId(null);
        }}
      >
        <header className="spl-head">
          {/* Ligne 1 : ◆ + TITRE à gauche — badge du modèle tout à droite
              (structure demandée 2026-08-15). Ligne 2 : réglages étiquetés
              (Mode de saisie · Compte), espacée du titre. */}
          <div className="spl-head__row">
            {/* Poignée de drag DÉDIÉE (2026-08-16) : draggable sur tout
                l'en-tête, l'input du titre avalait le geste. */}
            {canWrite && (
              <span
                className="spl-grip" title={t('scenarii:servicePlan.dragCard', 'Glisser pour réorganiser')}
                draggable
                onDragStart={(e) => { dragRef.current = card.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', card.id); }}
                onDragEnd={() => { dragRef.current = null; setDropId(null); }}
              >⋮⋮</span>
            )}
            <button
              type="button" className="spl-mark"
              title={agg ? t('scenarii:productPlan.expand', 'Détailler') : t('scenarii:productPlan.collapse', 'Agréger')}
              onClick={() => setAggs((s0) => { const n = new Set(s0); if (n.has(card.id)) n.delete(card.id); else n.add(card.id); return n; })}
            >
              {/* Mini-péristyle : carte agrégée = cour pleine ; carte ouverte =
                  cour évidée, comme si on avait zoomé et FRANCHI l'enceinte
                  (jeu de marque demandé le 2026-08-16). */}
              <svg className={`spl-mark__logo${agg ? '' : ' is-open'}`} viewBox="0 0 64 64" width="16" height="16" aria-hidden>
                <g fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round">
                  <path d="M25 52 h-7 a6 6 0 0 1 -6 -6 v-28 a6 6 0 0 1 6 -6 h28 a6 6 0 0 1 6 6 v28 a6 6 0 0 1 -6 6 h-7" />
                  <rect className="spl-mark__court" x="24" y="24" width="16" height="16" rx="3" fill="currentColor" stroke="none" />
                </g>
              </svg>
            </button>
            <input
              className="spl-catname" value={card.name || ''} disabled={!canWrite}
              placeholder={kindLabel(card)}
              onChange={(e) => update((d) => setServiceCardField(d, card.id, 'name', e.target.value))} />
            {agg && (
              <span className="spl-aggsum">
                <span className="spl-spark" aria-hidden>
                  {(isMonthly ? cols : MONTHS.map((_, m) => m)).slice(0, 12).map((c) => {
                    const vals = (isMonthly ? cols : MONTHS.map((_, m) => m)).map((cc) => caAt(cc));
                    const mx = Math.max(...vals, 0.0001);
                    return <i key={c} style={{ height: `${Math.max(2, Math.round(18 * caAt(c) / mx))}px` }} />;
                  })}
                </span>
                {pct && <span className="spl-aggpct">{pct}</span>}
                <span className="spl-aggtot">{fmt(caY)}<small>kCHF</small></span>
              </span>
            )}
            <span className="sp-kindtag spl-head__tag">{kindLabel(card)}</span>
            {canWrite && (
              <button className="spl-cardx" title={t('scenarii:servicePlan.delCard', 'Supprimer la catégorie')}
                onClick={() => setConfirmDel(card)}>
                <span aria-hidden>✕</span>
              </button>
            )}
          </div>
          {!agg && (
            <div className="spl-head__cfg">
              {card.kind === 'presta' && (
                <>
                  {/* Deux pilules (2026-08-16) : l'UNITÉ du volume, et la façon
                      de le saisir — le % n'est pas une unité. */}
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                    <span className="spl-pillseg" role="group">
                      {[['h', t('scenarii:servicePlan.uH', 'Heures')],
                        ['jh', t('scenarii:servicePlan.uJH', 'Jours-hommes')]].map(([u, lbl]) => (
                        <button
                          key={u} type="button" disabled={!canWrite}
                          className={(card.unit || 'h') === u ? 'on' : ''}
                          onClick={() => update((d) => setPrestaUnit(d, card.id, u))}>
                          {lbl}
                        </button>
                      ))}
                    </span>
                  </span>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                    <span className="spl-pillseg" role="group">
                      {[['month', t('scenarii:servicePlan.eQty', 'Qté')],
                        ['chf', t('scenarii:servicePlan.eKchf', 'kCHF')],
                        ['env', '%']].map(([e, lbl]) => (
                        <button
                          key={e} type="button" disabled={!canWrite}
                          className={prestaEntry(card) === e ? 'on' : ''}
                          onClick={() => update((d) => setPrestaEntry(d, card.id, e))}>
                          {lbl}
                        </button>
                      ))}
                    </span>
                  </span>
                </>
              )}
              {card.kind === 'recurring' && (
                <>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                    <span className="spl-pillseg" role="group">
                      <button type="button" className="on" disabled>{t('scenarii:servicePlan.uContract', 'Nb de contrats')}</button>
                    </span>
                  </span>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                    <span className="spl-pillseg" role="group">
                      {[['qty', t('scenarii:servicePlan.hbQty2', 'Qté')],
                        ['chf', t('scenarii:servicePlan.eKchf', 'kCHF')],
                        ['env', '%']].map(([u, lbl]) => (
                        <button
                          key={u} type="button" disabled={!canWrite}
                          className={recurringEntry(card) === u ? 'on' : ''}
                          onClick={() => update((d) => convertRecurringCard(d, card.id, u))}>
                          {lbl}
                        </button>
                      ))}
                    </span>
                  </span>
                </>
              )}
              {card.kind === 'prepaid' && (
                <>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                    {/* On compte des CONTRATS (services managés, abonnements
                        facturés d'avance) — décision Olivier 2026-08-16. */}
                    <span className="spl-pillseg" role="group">
                      <button type="button" className="on" disabled>{t('scenarii:servicePlan.uContract', 'Nb de contrats')}</button>
                    </span>
                  </span>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                    <span className="spl-pillseg" role="group">
                      {[['qty', t('scenarii:servicePlan.hbQty2', 'Qté')],
                        ['chf', t('scenarii:servicePlan.eKchf', 'kCHF')],
                        ['env', '%']].map(([u, lbl]) => (
                        <button
                          key={u} type="button" disabled={!canWrite}
                          className={prepaidEntry(card) === u ? 'on' : ''}
                          onClick={() => update((d) => convertPrepaidCard(d, card.id, u))}>
                          {lbl}
                        </button>
                      ))}
                    </span>
                  </span>
                </>
              )}
              {card.kind === 'hourbank' && (
                <>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                    {/* Un carnet d'heures se compte en HEURES — pas d'autre
                        unité (décision 2026-08-16). */}
                    <span className="spl-pillseg" role="group">
                      <button type="button" className="on" disabled>{t('scenarii:servicePlan.uH', 'Heures')}</button>
                    </span>
                  </span>
                  <span className="spl-cfg">
                    <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                    <span className="spl-pillseg" role="group">
                      {[['qty', t('scenarii:servicePlan.hbQty2', 'Qté')],
                        ['chf', t('scenarii:servicePlan.eKchf', 'kCHF')],
                        ['env', '%']].map(([u, lbl]) => (
                        <button
                          key={u} type="button" disabled={!canWrite}
                          className={hourbankEntry(card) === u ? 'on' : ''}
                          onClick={() => update((d) => convertHourbankCard(d, card.id, u))}>
                          {lbl}
                        </button>
                      ))}
                    </span>
                  </span>
                </>
              )}
              <span className="spl-cfg">
                <label>{t('scenarii:productPlan.accountShort', 'Compte')}</label>
                <select
                  className={`spl-acct${card.lineId ? '' : ' spl-acct--todo'}`}
                  value={card.lineId || ''} disabled={!canWrite}
                  onChange={(e) => update((d) => setServiceCardField(d, card.id, 'lineId', e.target.value))}>
                  <option value="">{t('scenarii:productPlan.pickLine', 'à choisir…')}</option>
                  {revAccounts.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                {(card.kind === 'prepaid' || card.kind === 'hourbank') ? (
                  <span className="sp-terms-fixed">{t('scenarii:servicePlan.atBilling', 'à la facturation')}</span>
                ) : (
                  <select
                    className="spl-acct" value={Number(card.terms) || 0} disabled={!canWrite}
                    onChange={(e) => update((d) => setServiceCardField(d, card.id, 'terms', Number(e.target.value)))}>
                    {TERMS.map((j) => <option key={j} value={j}>{j === 0 ? t('scenarii:servicePlan.cash', 'comptant') : `à ${j} jours`}</option>)}
                  </select>
                )}
              </span>
            </div>
          )}
        </header>

        {!agg && (
        <div className="spl-scroll lk-hscroll">
          <table className="spl-table spl-table--fixed">
            <colgroup>
              <col style={{ width: 190 }} />
              {params.map((p) => <col key={p.key} style={{ width: 240 / params.length }} className={['cost', 'costPct'].includes(p.key) ? 'spl-col-cost' : 'spl-col-price'} />)}
              <col style={{ width: 10 }} />
              {cols.map((c) => <col key={c} />)}
              {isMonthly && <col style={{ width: 84 }} />}
            </colgroup>
            <thead>
              {/* L'INDUCTEUR mensuel en clair au-dessus des mois : on doit
                  voir ce que sont les cellules (kCHF, jours-hommes, %…). */}
              <tr className="spl-indrow">
                <th className="spl-c-lbl" />
                {params.map((p) => <th key={p.key} className="spl-c-unit" />)}
                <th className="spl-c-sep" />
                {/* La colonne TOTAL partage désormais la même unité que les
                    mois (Σ des volumes SAISIS, jan→déc — décision du
                    2026-08-13, cf. `entryVal`) : elle rejoint le bandeau,
                    plus de « kCHF » à part. */}
                {indBand(monthUnit, cols)}
              </tr>
              <tr>
                <th className="spl-c-lbl">{t('scenarii:servicePlan.rowLabel', 'Libellé')}</th>
                {params.map((p) => (
                  <th key={p.key} className="spl-c-unit" title={p.title || (p.ext ? t('scenarii:servicePlan.matOnly', 'Coût MATÉRIEL uniquement (fournitures, matériel revendu) — le personnel est compté dans People, il ne descend pas ici.') : undefined)}>
                    {p.label}
                  </th>
                ))}
                <th className="spl-c-sep" />
                {cols.map((c) => (
                  <th key={c} className={colCls(c)}>
                    {isMonthly ? MONTHS[c] : c}
                  </th>
                ))}
                {isMonthly && <th className="spl-c-tot">{focusYear}</th>}
              </tr>
            </thead>
            <tbody>
              {!agg && rows.map((r) => (
                <tr key={r.id}>
                  <td className="spl-c-lbl">
                    <input
                      className="spl-rowname" value={r.name || ''} disabled={!canWrite}
                      placeholder={t('scenarii:servicePlan.rowPh', 'ex. client')}
                      onChange={(e) => update((d) => setServiceRowField(d, card.id, r.id, 'name', e.target.value))} />
                    {canWrite && (
                      <button className="spl-rowx" tabIndex={-1} title={t('scenarii:servicePlan.delRow', 'Supprimer')}
                        onClick={() => update((d) => delServiceRow(d, card.id, r.id))}>✕</button>
                    )}
                  </td>
                  {params.map((p) => <td key={p.key} className="spl-c-unit">{paramCell(r, p)}</td>)}
                  <td className="spl-c-sep" />
                  {cols.map((c) => (
                    <td key={c} className={colCls(c)}>
                      {monthCell(r, c)}
                    </td>
                  ))}
                  {isMonthly && (() => {
                    // Lignes saisies en % : le total porte le signe % et vire
                    // au rouge dès qu'il dépasse 100 (demande 2026-08-16).
                    if (card.kind === 'project') {
                      const tp = projectPctTotal(r);
                      return <td className={`spl-c-tot${tp > 100.0001 ? ' spl-c-tot--over' : ''}`}>{`${fmt1(tp) || 0} %`}</td>;
                    }
                    if (isPctRow(r)) {
                      const tp = pctTotOf(r);
                      return <td className={`spl-c-tot${tp > 100.0001 ? ' spl-c-tot--over' : ''}`}>{`${fmt1(tp) || 0} %`}</td>;
                    }
                    // Sinon : Σ des volumes SAISIS jan→déc (même unité que les
                    // mois), pas le CA qui en découle.
                    return <td className="spl-c-tot">{fmt1(cols.reduce((s, c) => s + entryVal(r, c), 0)) || '–'}</td>;
                  })()}
                </tr>
              ))}
              {canWrite && !agg && (
                <tr className="spl-addrow">
                  <td className="spl-c-lbl">
                    <button onClick={() => update((d) => addServiceRow(d, card.id))}>
                      ＋ {t('scenarii:servicePlan.addRow', 'Ligne')}
                    </button>
                  </td>
                  {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                  <td className="spl-c-sep" />
                  {cols.map((c) => <td key={c} className={colCls(c)} />)}
                  {isMonthly && <td className="spl-c-tot" />}
                </tr>
              )}
              {volUnit && card.kind !== 'project' && !agg && (
                <tr className="spl-sum spl-sum--first">
                  <td className="spl-c-lbl">{volUnit}</td>
                  {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                  <td className="spl-c-sep" />
                  {(() => {
                    // Somme (toutes lignes) par cellule — même calcul pour les
                    // colonnes de mois et pour le TOTAL annuel (Σ des mois) ;
                    // avant, la cellule total restait vide (bug signalé le
                    // 2026-08-13 : « le total ne calcule rien »).
                    const volAt = (c) => rows.reduce((s, r) => {
                      if (card.kind === 'presta') {
                        if (rowEnv(r)) {
                          if (!isMonthly) return s + (Number(r.volume?.[c]) || 0);
                          return s + (Number(r.volume?.[focusYear]) || 0) * (annualVolPct(r, focusYear, c) / 100);
                        }
                        return s + (isMonthly ? productRowQtyMonth(r, focusYear, c) : productRowQtyYear(r, c));
                      }
                      if (card.kind === 'annualvol') {
                        if (!isMonthly) return s + (Number(r.volume?.[c]) || 0);
                        return s + (Number(r.volume?.[focusYear]) || 0) * (annualVolPct(r, focusYear, c) / 100);
                      }
                      if (card.kind === 'prepaid') {
                        if (!isMonthly) return s + MONTHS.reduce((t2, _, m) => t2 + prepaidQtyMonth(r, c, m), 0);
                        return s + prepaidQtyMonth(r, focusYear, c);
                      }
                      if (card.kind === 'hourbank') {
                        if (!isMonthly) return s + MONTHS.reduce((t2, _, m) => t2 + hourbankQtyAt(card, r, c, m), 0);
                        return s + hourbankQtyAt(card, r, focusYear, c);
                      }

                      if (!isMonthly) return s + (card.kind === 'regie' ? productRowQtyYear(r, c) : heldQtyMonth(r, c, 11));
                      return s + (card.kind === 'regie' ? productRowQtyMonth(r, focusYear, c) : heldQtyMonth(r, focusYear, c));
                    }, 0);
                    return (
                      <>
                        {cols.map((c) => <td key={c} className={colCls(c)}>{fmt1(volAt(c)) || '–'}</td>)}
                        {isMonthly && <td className="spl-c-tot">{fmt1(cols.reduce((s, c) => s + volAt(c), 0)) || '–'}</td>}
                      </>
                    );
                  })()}
                </tr>
              )}
              {(card.kind === 'prepaid' || card.kind === 'hourbank') && !agg && isMonthly && metric === 'ca' && (
                <tr className="spl-sum spl-sum--first">
                  <td className="spl-c-lbl">{t('scenarii:servicePlan.billedSum', 'Facturé')}<span className="spl-unit">kCHF</span></td>
                  {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                  <td className="spl-c-sep" />
                  {(() => {
                    const billedAt = (r, c) => (card.kind === 'hourbank'
                      ? hourbankBilledMonth(model, card, r, focusYear, c)
                      : prepaidBilledAt(card, r, focusYear, c));
                    return (
                      <>
                        {cols.map((c) => {
                          const b = rows.reduce((s2, r) => s2 + billedAt(r, c), 0);
                          return <td key={c} className={colCls(c)}>{fmt(b) === '0' ? '–' : fmt(b)}</td>;
                        })}
                        <td className="spl-c-tot">{(() => { const b = cols.reduce((s2, c) => s2 + rows.reduce((s3, r) => s3 + billedAt(r, c), 0), 0); return fmt(b) === '0' ? '–' : fmt(b); })()}</td>
                      </>
                    );
                  })()}
                </tr>
              )}
              {card.kind === 'hourbank' && !agg && (
                <tr className="spl-sum">
                  <td className="spl-c-lbl">{t('scenarii:servicePlan.hoursSold', 'Heures')} {t('scenarii:servicePlan.sold', 'vendues')}</td>
                  {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                  <td className="spl-c-sep" />
                  {(() => {
                    const hAt = (c) => (isMonthly
                      ? serviceCardHoursMonth(model, card, focusYear, c)
                      : MONTHS.reduce((s2, _, m) => s2 + serviceCardHoursMonth(model, card, c, m), 0));
                    return (
                      <>
                        {cols.map((c) => <td key={c} className={colCls(c)}>{fmt1(hAt(c)) || '–'}</td>)}
                        {isMonthly && <td className="spl-c-tot">{fmt1(cols.reduce((s2, c) => s2 + hAt(c), 0)) || '–'}</td>}
                      </>
                    );
                  })()}
                </tr>
              )}
              <tr className="spl-sum spl-sum--ca">
                <td className="spl-c-lbl">{metricLabel(metric, card, t)}<span className="spl-unit">kCHF</span></td>
                {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(caAt(c)) === '0' ? '–' : fmt(caAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(caY) === '0' ? '–' : fmt(caY)}</td>}
              </tr>
              {!agg && metric === 'ca' && (
              <tr className="spl-sum">
                <td className="spl-c-lbl">{t('scenarii:productPlan.cogsMat', 'Coût matériel')}<span className="spl-unit">kCHF</span></td>
                {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(crAt(c)) === '0' ? '–' : fmt(crAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(crY) === '0' ? '–' : fmt(crY)}</td>}
              </tr>
              )}
              {!agg && metric === 'ca' && (
              <tr className="spl-sum spl-sum--marge">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.margin', 'Marge')}
                  {pct && <span className="spl-pct">{pct}</span>}
                  {pctOver && <span className="sp-over">{t('scenarii:servicePlan.pctOver', 'Σ % > 100')}</span>}
                </td>
                {params.map((p) => <td key={p.key} className="spl-c-unit" />)}
                <td className="spl-c-sep" />
                {cols.map((c) => {
                  const m = caAt(c) - crAt(c);
                  return <td key={c} className={colCls(c)}>{fmt(m) === '0' ? '–' : fmt(m)}</td>;
                })}
                {isMonthly && <td className="spl-c-tot">{fmt(caY - crY) === '0' ? '–' : fmt(caY - crY)}</td>}
              </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </section>
    );
  };

  return (
    <RoCtx.Provider value={!canWrite}>
      <PageShell timeless embedded={embedded} exportRef={exportRef} className="page-shell--flush" title={t('scenarii:servicePlan.title', 'Plan services')}>
        <div className="spl-bar">
          <div className="spl-seg">
            {[['m0', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year: cur })],
              ['m1', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year: cur + 1 })]].map(([k, lbl]) => (
              /* La vue « Plan pluriannuel » a quitté l'Ancienne prévision (2026-09-12) :
                 le pluriannuel vit dans la Trajectoire. */
              <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k)}>{lbl}</button>
            ))}
          </div>
          {canWrite && cards.length > 0 && (
            <span className="sp-addwrap">
              <button className="spl-add spl-add--primary" onClick={() => setMenuOpen((v) => !v)}>
                ＋ {t('scenarii:servicePlan.addCard', 'Modèle de service')}
              </button>
              {menuOpen && modelMenu}
            </span>
          )}
          {cards.length > 0 && (
            <div className="spl-seg spl-seg--metric">
              {[['ca', t('scenarii:servicePlan.mCa', 'Revenus')],
                ['billed', t('scenarii:servicePlan.mBilled', 'Facturé')],
                ['cash', t('scenarii:servicePlan.mCash', 'Encaissé')]].map(([k, lbl]) => (
                <button key={k} className={metric === k ? 'on' : ''} onClick={() => setMetric(k)}>{lbl}</button>
              ))}
            </div>
          )}
          {cards.length > 0 && (
            <button className="spl-add spl-add--quiet" onClick={() => setAggs((s0) => (s0.size >= cards.length ? new Set() : new Set(cards.map((c) => c.id))))}>
              {aggs.size >= cards.length ? t('scenarii:servicePlan.expandAll', 'Tout déplier') : t('scenarii:servicePlan.collapseAll', 'Tout replier')}
            </button>
          )}
        </div>

        {!cards.length && !cardsProduits.length ? (
          <section className="spl-start">
            <h2>{t('scenarii:servicePlan.startTitle', 'Planifiez vos ventes de services')}</h2>
            <p className="sp-startp">{t('scenarii:servicePlan.startBody', 'Choisis un modèle — chaque card fait le calcul pour toi et alimente le compte de chiffre d\'affaires que tu lui attribues.')}</p>
            {canWrite && modelMenu}
          </section>
        ) : (
          <>
            {cards.map(renderCard)}
            <ProductPlanPage host={{ model, update, revLines, view }} activeTenant={activeTenant} addToast={addToast}
                             canRead={canRead} canWrite={canWrite} />
          </>
        )}

        {/* SYNTHÈSE EN PIED (2026-08-31) : inducteur (heures de travail
            vendues, tous modèles) + CA total du plan. Elle ouvrait la page
            depuis le 2026-08-16 ; on lit désormais les cards, puis ce qu'elles
            totalisent — un total se pose sous ce qu'il additionne. Mois
            alignés sur les cards (même colgroup 190 + 240 + 10). */}
        {cards.length > 0 && (() => {
          const pcols = isMonthly ? MONTHS.map((_, m) => m) : years;
          const hAt = (c) => (isMonthly
            ? servicePlanHoursMonth(model, focusYear, c)
            : MONTHS.reduce((s2, _, m) => s2 + servicePlanHoursMonth(model, c, m), 0));
          const caAt = (c) => cards.reduce((s2, cd) => s2 + (isMonthly
            ? metricMonth(model, cd, metric, focusYear, c)
            : MONTHS.reduce((s3, _, m) => s3 + metricMonth(model, cd, metric, c, m), 0)), 0);
          const crAt = (c) => cards.reduce((s2, cd) => s2 + (cd.rows || []).reduce((s3, r) => s3 + (isMonthly
            ? serviceRowCostMonth(model, cd, r, focusYear, c)
            : MONTHS.reduce((s4, _, m) => s4 + serviceRowCostMonth(model, cd, r, c, m), 0)), 0), 0);
          const hTot = pcols.reduce((s2, c) => s2 + hAt(c), 0);
          const caTot = pcols.reduce((s2, c) => s2 + caAt(c), 0);
          const crTot = pcols.reduce((s2, c) => s2 + crAt(c), 0);

          const hasCost = crTot !== 0 && metric === 'ca';
          const showHours = metric === 'ca';
          const nSynthRows = 1 + (synthDetail ? cards.length : 0) + (hasCost ? 2 : 0) + (showHours ? 1 : 0);
          const margePct = caTot ? `${(Math.round(1000 * (caTot - crTot) / caTot) / 10).toLocaleString('de-CH')} %` : null;
          return (
            <section className="spl-card spl-card--hours spl-card--synth">
              <div className="spl-scroll lk-hscroll">
                <table className="spl-table spl-table--fixed">
                  <colgroup>
                    <col style={{ width: 190 }} />
                    <col style={{ width: 240 }} />
                    <col style={{ width: 10 }} />
                    {pcols.map((c) => <col key={c} />)}
                    {/* La colonne du total annuel n'avait AUCUNE largeur ici :
                        elle s'écrasait et le total de l'année devenait illisible
                        (constat Olivier 2026-08-17). Même 84 px que les cards. */}
                    {isMonthly && <col style={{ width: 84 }} />}
                  </colgroup>
                  <thead>
                    <tr className="spl-indrow">
                      <th className="spl-c-lbl" />
                      <th className="spl-c-unit" />
                      <th className="spl-c-sep" />
                      {indBand('', pcols)}
                    </tr>
                    <tr>
                      {/* Bascule « Par carte » à hauteur des mois, au-dessus
                          des figures-clés (retour Olivier 2026-08-17). */}
                      <th className="spl-c-lbl spl-th-toggle">
                        <button type="button" className="spl-synth-toggle" onClick={() => setSynthDetail((v) => !v)}>
                          {synthDetail ? t('scenarii:servicePlan.hideCards', 'Masquer les cartes') : t('scenarii:servicePlan.byCard', 'Par carte')}
                        </button>
                      </th>
                      <th className="spl-c-unit" />
                      <th className="spl-c-sep" />
                      {pcols.map((c) => (
                        <th key={c} className={colCls(c)}>{isMonthly ? MONTHS[c] : c}</th>
                      ))}
                      {isMonthly && <th className="spl-c-tot">{focusYear}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="spl-sum spl-sum--ca">
                      {/* Zone morte de gauche = figures-clés annuelles (proto B, 2026-08-16). */}
                      <td className="spl-synth-side" rowSpan={nSynthRows}>
                        <span className="spl-skpi"><b>{fmt(caTot)}</b><small>kCHF</small></span>
                        {hasCost && margePct && <span className="spl-skpi spl-skpi--marge"><b>{margePct}</b><small>{t('scenarii:servicePlan.margeLc', 'marge')}</small></span>}
                        {showHours && hTot !== 0 && <span className="spl-skpi spl-skpi--h"><b>{fmt(hTot)}</b><small>{t('scenarii:servicePlan.hoursSoldLc', 'h vendues')}</small></span>}
                      </td>
                      <td className="spl-c-lbl">{metric === 'billed' ? t('scenarii:servicePlan.mBilled', 'Facturé')
                        : metric === 'cash' ? t('scenarii:servicePlan.mCash', 'Encaissé')
                          : t('scenarii:servicePlan.caShort', 'CA')}<span className="spl-unit">kCHF</span></td>
                      <td className="spl-c-sep" />
                      {pcols.map((c) => <td key={c} className={colCls(c)}>{fmt(caAt(c)) === '0' ? '–' : fmt(caAt(c))}</td>)}
                      {isMonthly && <td className="spl-c-tot">{fmt(caTot) === '0' ? '–' : fmt(caTot)}</td>}
                    </tr>
                    {synthDetail && cards.map((cd) => {
                      const cAt = (c) => (isMonthly
                        ? metricMonth(model, cd, metric, focusYear, c)
                        : MONTHS.reduce((s3, _, m) => s3 + metricMonth(model, cd, metric, c, m), 0));
                      const cTot = pcols.reduce((s3, c) => s3 + cAt(c), 0);
                      return (
                        <tr key={cd.id} className="spl-synth-card">
                          <td className="spl-c-lbl">{cd.name || kindLabel(cd)}</td>
                          <td className="spl-c-sep" />
                          {pcols.map((c) => <td key={c} className={colCls(c)}>{fmt(cAt(c)) === '0' ? '–' : fmt(cAt(c))}</td>)}
                          {isMonthly && <td className="spl-c-tot">{fmt(cTot) === '0' ? '–' : fmt(cTot)}</td>}
                        </tr>
                      );
                    })}
                    {hasCost && (
                    <tr className="spl-sum">
                      <td className="spl-c-lbl">{t('scenarii:servicePlan.costShort2', 'Coût')}<span className="spl-unit">kCHF</span></td>
                      <td className="spl-c-sep" />
                      {pcols.map((c) => <td key={c} className={colCls(c)}>{fmt(crAt(c)) === '0' ? '–' : fmt(crAt(c))}</td>)}
                      {isMonthly && <td className="spl-c-tot">{fmt(crTot) === '0' ? '–' : fmt(crTot)}</td>}
                    </tr>
                    )}
                    {hasCost && (
                    <tr className="spl-sum spl-sum--marge">
                      <td className="spl-c-lbl">
                        {t('scenarii:productPlan.margin', 'Marge')}
                        {margePct && <span className="spl-pct">{margePct}</span>}
                      </td>
                      <td className="spl-c-sep" />
                      {pcols.map((c) => { const m = caAt(c) - crAt(c); return <td key={c} className={colCls(c)}>{fmt(m) === '0' ? '–' : fmt(m)}</td>; })}
                      {isMonthly && <td className="spl-c-tot">{fmt(caTot - crTot) === '0' ? '–' : fmt(caTot - crTot)}</td>}
                    </tr>
                    )}
                    {showHours && (
                    <tr className="spl-sum spl-sum--first">
                      <td className="spl-c-lbl">{t('scenarii:servicePlan.hoursShort2', 'Heures')}</td>
                      <td className="spl-c-sep" />
                      {pcols.map((c) => <td key={c} className={colCls(c)}>{fmt1(hAt(c)) || '–'}</td>)}
                      {isMonthly && <td className="spl-c-tot">{fmt1(hTot) || '–'}</td>}
                    </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })()}


      {confirmDel && (
        <div className="spl-confirm" role="dialog" aria-modal="true">
          <div className="spl-confirm__box">
            <p>
              {t('scenarii:servicePlan.delConfirm', 'Supprimer la carte « {{name}} » et toutes ses lignes ?', { name: confirmDel.name || kindLabel(confirmDel) })}
            </p>
            <div className="spl-confirm__btns">
              <button type="button" onClick={() => setConfirmDel(null)}>{t('common:cancel', 'Annuler')}</button>
              <button
                type="button" className="spl-confirm__del"
                onClick={() => { update((d) => delServiceCard(d, confirmDel.id)); setConfirmDel(null); }}>
                {t('scenarii:servicePlan.delCardBtn', 'Supprimer')}
              </button>
            </div>
          </div>
        </div>
      )}
      </PageShell>
    </RoCtx.Provider>
  );
}
